import { createApi } from '@reduxjs/toolkit/query/react';
import { axiosBaseQuery } from 'modules/api/utils';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';

import {
  mapBlobToConversationInsights,
  mapBlobToExecutiveSummary,
  mapBlobToKeyMetrics,
  mapBlobToSessionExploration,
} from './mappers';

import type {
  NatalieReportData,
  ReportDataBlob,
  ReportDataDownload,
  ReportRequestArgs,
  ReportingApiReport,
} from './types';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

const PIN_HEADER = 'X-Report-Pin';

const getBaseUrl = () => configService.getUrl(UrlNames.ReportingApi);

const toQueryError = (status: number, data: unknown): { error: FetchBaseQueryError } => ({
  error: { status, data } as FetchBaseQueryError,
});

/**
 * Bare `fetch` for PIN-authenticated and S3 requests, deliberately bypassing `ajaxService`:
 *
 * - `ajaxService` attaches `Authorization: Bearer …`, which invalidates an S3 presigned URL's
 *   query signature.
 * - Its response interceptor calls `keycloak.logout()` on any 401. A wrong PIN on the public
 *   report page returns 401, so routing PIN requests through it would sign out a logged-in
 *   visitor who followed a shared link.
 */
const pinFetch = async <T>(path: string, pin: string | undefined, signal?: AbortSignal) => {
  try {
    const response = await fetch(`${getBaseUrl()}${path}`, {
      headers: pin ? { [PIN_HEADER]: pin } : undefined,
      signal,
    });

    if (!response.ok) {
      return toQueryError(response.status, await response.text().catch(() => response.statusText));
    }

    return { data: (await response.json()) as T };
  } catch (error) {
    if (signal?.aborted) return toQueryError(499, 'Request aborted');

    return toQueryError(500, error instanceof Error ? error.message : 'Network error');
  }
};

export const reportingApi = createApi({
  reducerPath: 'reportingApi',
  tagTypes: ['REPORT', 'REPORT_DATA'],
  baseQuery: axiosBaseQuery({ baseURL: getBaseUrl }),
  endpoints: (builder) => ({
    getReport: builder.query<ReportingApiReport, ReportRequestArgs>({
      providesTags: (_result, _error, { reportId }) => [{ type: 'REPORT', id: reportId }],
      queryFn: async ({ reportId, pin }, api, _extraOptions, baseQuery) => {
        if (pin) return pinFetch<ReportingApiReport>(`/v1/reports/${reportId}`, pin, api.signal);

        return baseQuery({ url: `/v1/reports/${reportId}` }) as Promise<
          { data: ReportingApiReport } | { error: FetchBaseQueryError }
        >;
      },
    }),

    /**
     * Two hops: reporting-api hands back a presigned S3 URL, then the payload is fetched
     * straight from S3. Deliberately one endpoint rather than two so the presigned URL — valid
     * for an hour — is never itself cached; an expired signature can only be hit mid-flight,
     * and a refetch mints a fresh one.
     *
     * The blob averages ~110 kB and reaches ~1.1 MB in prod, so it is fetched once per report
     * and sliced into the three tab view models by the providers rather than re-requested.
     */
    getReportData: builder.query<NatalieReportData, ReportRequestArgs>({
      providesTags: (_result, _error, { reportId }) => [{ type: 'REPORT_DATA', id: reportId }],
      queryFn: async ({ reportId, pin }, api, _extraOptions, baseQuery) => {
        const path = `/v1/reports/${reportId}/data`;

        const downloadResult = pin
          ? await pinFetch<ReportDataDownload>(path, pin, api.signal)
          : ((await baseQuery({ url: path })) as { data: ReportDataDownload } | { error: FetchBaseQueryError });

        if ('error' in downloadResult) return downloadResult;

        const { downloadUrl } = downloadResult.data;

        if (!downloadUrl) return toQueryError(502, 'Report data response contained no download URL');

        let blob: ReportDataBlob;

        try {
          // No credentials: the presigned URL carries its own signature and S3 rejects a
          // request that also presents an Authorization header.
          const response = await fetch(downloadUrl, { signal: api.signal });

          if (!response.ok) return toQueryError(response.status, 'Failed to download report data');

          blob = (await response.json()) as ReportDataBlob;
        } catch (error) {
          if (api.signal?.aborted) return toQueryError(499, 'Request aborted');

          return toQueryError(502, error instanceof Error ? error.message : 'Failed to download report data');
        }

        return {
          data: {
            executiveSummary: mapBlobToExecutiveSummary(blob),
            conversationInsights: mapBlobToConversationInsights(blob),
            sessionExploration: mapBlobToSessionExploration(blob),
            keyMetrics: mapBlobToKeyMetrics(blob),
          },
        };
      },
    }),

    updateReport: builder.mutation<ReportingApiReport, Pick<ReportingApiReport, 'reportId' | 'name'>>({
      invalidatesTags: (_result, _error, { reportId }) => [{ type: 'REPORT', id: reportId }],
      query: ({ reportId, name }) => ({ url: `/v1/reports/${reportId}`, method: 'PUT', data: { name } }),
    }),
  }),
});

export const { useGetReportQuery, useLazyGetReportQuery, useGetReportDataQuery, useUpdateReportMutation } =
  reportingApi;
